import { AutoIsoObjectType } from '../types/AutoIsoModel';

/**
 * Ghost-entity filter configuration.
 *
 * ─── What this file is ────────────────────────────────────────────────────
 *
 * This is the SINGLE external knob for the ghost-entity filter. To tune the
 * filter for a sport (idle threshold, jitter epsilon, ball/puck exemption,
 * remove vs. dim) edit only this file. The pure-function logic lives in
 * ../utils/ghostFilter.ts and never needs to change when you tweak a value
 * here.
 *
 * ─── How to enable a new sport ────────────────────────────────────────────
 *
 * Add a new entry to PER_LEAGUE keyed by the value of `league.name` in the
 * database (case-sensitive). Anything not listed inherits DEFAULT_CONFIG
 * which has `enabled: false` — i.e. zero-overhead pass-through, no filtering.
 */

export type GhostFilterMode = 'remove' | 'dim';

export interface GhostFilterConfig {
  /** Master toggle. When false the filter is a no-op and entities pass through untouched. */
  enabled: boolean;
  /** An entity is considered a ghost after this many milliseconds without crossing positionEpsilon. */
  idleMs: number;
  /** Movement threshold in surface units (typically feet). Position deltas below this count as jitter, not motion. */
  positionEpsilon: number;
  /**
   * Hysteresis: once an entity has been classified as a ghost, it needs to move at least this much
   * in a single tick to be considered "live" again. Set LARGER than positionEpsilon. Without this
   * separation, jitter that crosses positionEpsilon causes rapid mount/unmount flicker (the entity
   * oscillates between live and ghost as the noisy reading dances over the threshold).
   * A real step is ~2-3 ft; sensor jitter is sub-1-ft. 2.0 separates them cleanly.
   */
  wakeEpsilon: number;
  /** Entity types that are never filtered (e.g. the ball — it can legitimately stop). */
  exemptObjectTypes: AutoIsoObjectType[];
  /** 'remove' drops ghosts from the rendered list; 'dim' keeps them but exposes ghostIds for the renderer to lower opacity on. */
  mode: GhostFilterMode;
  /** Opacity to apply to ghost markers when mode === 'dim'. 0..1. Renderer reads this to style ghosts. */
  dimOpacity: number;
}

const DEFAULT_CONFIG: GhostFilterConfig = {
  enabled: false,
  idleMs: 1500,
  positionEpsilon: 0.5,
  wakeEpsilon: 2.0,
  exemptObjectTypes: [AutoIsoObjectType.BallOrPuck],
  mode: 'remove',
  dimOpacity: 0.3,
};

/**
 * Per-league overrides. Anything omitted falls back to DEFAULT_CONFIG.
 * Keys must match `league.name` in the DB exactly (case-sensitive).
 *
 *   NBA              — basketball (Hawkeye tracking)
 *   HOCKEY_NHL       — hockey   (SMT tracking, filter off by default)
 *   PREMIER_LEAGUE   — soccer   (filter off)
 *   NFL              — football (filter off)
 */
const PER_LEAGUE: Record<string, Partial<GhostFilterConfig>> = {
  NBA: {
    enabled: true,
    idleMs: 1500,
    positionEpsilon: 0.1,
    // Real player motion (a step) is ~2-3 ft per tick. Tracker jitter is < 1 ft.
    // 2.0 ft cleanly separates them — ghosts only wake on actual motion.
    wakeEpsilon: 2.0,
    mode: 'remove',
  },
};

/** Resolve the active config for the current game's league. */
export function getGhostFilterConfig(leagueName: string | null | undefined): GhostFilterConfig {
  if (!leagueName) return DEFAULT_CONFIG;
  const overrides = PER_LEAGUE[leagueName];
  if (!overrides) return DEFAULT_CONFIG;
  return { ...DEFAULT_CONFIG, ...overrides };
}
